import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import utilServices from 'app/services/util-services'
import { selectItemById, updateTerm, getTermById } from 'app/redux/store/features/terms'
import UpdateTermForm from './UpdateTermForm'

const UpdateTerm = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const [apiCallInProgress, setApiCallInProgress] = useState(false)
    const { state } = useLocation()
    const { termId } = state
    const { terms, loading, error } = useSelector((state) => state.terms)
    const term = selectItemById(terms, termId)
    const [termData, setTermData] = useState({})
    const [prvTermId, setPrvTermId] = useState(term.prvTermId)

    useEffect(() => {
        if (utilServices.isNullOrUndefined(term.id)) dispatch(getTermById({ id: termId }))
    }, [])

    // `selectItemById` returns a new object each render; only (re)initialize local edit state
    // when we truly received a different term record, otherwise we would overwrite user edits.
    useEffect(() => {
        if (utilServices.isNullOrUndefined(term?.id)) return

        setTermData((prev) => {
            if (prev?.id === term.id) return prev
            return term
        })
        setPrvTermId((prev) => (prev === term.prvTermId ? prev : term.prvTermId))
    }, [term?.id])

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) handleError(error)
        else {
            enqueueSnackbar('Term updated successfully', {
                variant: 'success',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
            // Avoid an immediate refetch in `TermsGrid`; rely on the update payload already merged into Redux.
            navigate('/courses/setup/terms', { state: { navigateFrom: 'save-update-term' } })
        }
    }, [loading])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    const onFieldDataChanged = useCallback((e) => {
        const field = e?.dataField
        if (!field) return
        setTermData((prev) => ({ ...prev, [field]: e.value }))
    }, [])

    const onUpdateTerm = useCallback(async () => {
        setApiCallInProgress(true)
        dispatch(
            updateTerm({
                ...termData,
                dateStart: utilServices.toUtcStartOfDayForApi(termData.dateStart),
                dateEnd: utilServices.toUtcEndOfDayForApi(termData.dateEnd),
                prvTermId: prvTermId,
            })
        )
    }, [prvTermId, termData])

    return (
        <React.Fragment>
            <UpdateTermForm
                termData={termData}
                updateTerm={onUpdateTerm}
                prvTermId={prvTermId}
                setPrvTermId={setPrvTermId}
                onFieldDataChanged={onFieldDataChanged}
            />
        </React.Fragment>
    )
}

export default UpdateTerm
